import { PriceDropPace } from "@/Apollo/schema";

/**
 * Route of the create-ad flow. `?id=` holds the draft from the first action on. The "Place ad"
 * CTAs link here, and `/create-ad/wizard` without an id redirects here (config/redirects.js).
 */
export const CREATE_AD_V2_ROUTE = "/create-ad/v2";

/** Photos the seller has to add before the photos can be read. */
export const CREATE_AD_V2_REQUIRED_PHOTOS = 5;

/** Upper bound on photos. */
export const CREATE_AD_V2_MAX_PHOTOS = 15;

/**
 * Shortest buyer-facing description the gateway accepts (setListingField DESCRIPTION and the
 * publish gate), in characters after trimming.
 */
export const CREATE_AD_V2_MIN_DESCRIPTION_LENGTH = 80;

/** Longest note for the buyer the gateway keeps (BUYER_NOTE), in characters. */
export const CREATE_AD_V2_MAX_BUYER_NOTE_LENGTH = 1000;

/** Lowest asking price the gateway accepts (publish gate v2). */
export const CREATE_AD_V2_MIN_PRICE = 20;

/**
 * Highest euro amount any money field takes. The price columns are numeric(12, 2), so a longer
 * number fails the save with "numeric field overflow" instead of a message the seller can act on.
 */
export const CREATE_AD_V2_MAX_PRICE = 9_999_999;

/** One photo upload gives up after this long, so a stalled request never blocks the queue. */
export const CREATE_AD_V2_UPLOAD_TIMEOUT_MS = 60000;

/**
 * The one floor of an ad, as a share of the asking price: Smart Drop's "Never lower than" and,
 * with a fixed price, the lowest bid that reaches the seller. Prefilled at 70%, rounded down.
 */
export const CREATE_AD_V2_DEFAULT_FLOOR_SHARE = 0.7;

/** The gateway's publish gate: with bids on, the lowest bid is at least 10% of the asking price. */
export const CREATE_AD_V2_MIN_BID_SHARE = 0.1;

/** Smart Drop's step choices, in percent of the asking price; the seller may type 1 to 25. */
export const CREATE_AD_V2_SMART_DROP_STEPS = [5, 10, 15] as const;
export const CREATE_AD_V2_SMART_DROP_MIN_STEP = 1;
export const CREATE_AD_V2_SMART_DROP_MAX_STEP = 25;
export const CREATE_AD_V2_SMART_DROP_DEFAULT_STEP = 10;

/** Smart Drop's paces, in the order the chips show them. */
export const CREATE_AD_V2_SMART_DROP_PACES: readonly PriceDropPace[] = [
  PriceDropPace.Week,
  PriceDropPace.TwoWeeks,
  PriceDropPace.Month,
];
export const CREATE_AD_V2_SMART_DROP_DEFAULT_PACE = PriceDropPace.TwoWeeks;

/**
 * `generateListing` waits up to 25 s on the server; after a minute without an answer the
 * reading screen gives up and offers to try again or fill in the details.
 */
export const CREATE_AD_V2_GENERATION_TIMEOUT_MS = 60000;

/** While the photos are read, the next line of the checklist starts this often. */
export const CREATE_AD_V2_READING_STEP_MS = 5000;

/**
 * The checklist stays ticked off this long once the answer is in, then the result opens: long
 * enough to read the brand it found ("Brand checked: France & Søn"), not a flash (N1).
 */
export const CREATE_AD_V2_READING_SETTLE_MS = 2500;

/**
 * Categories (English slug) that never go by parcel whatever their size. Matched against the
 * category and its ancestors. Empty until the business names them: the spec refers to
 * "excluded" categories but no list exists in the gateway or the old wizard.
 */
export const CREATE_AD_V2_PARCEL_EXCLUDED_CATEGORY_SLUGS: readonly string[] =
  [];

/** Pause after the last keystroke before the price step asks for a new payout quote (as the apps). */
export const CREATE_AD_V2_PAYOUT_QUOTE_DEBOUNCE_MS = 300;

/** Screen-out duration before the next screen slides in. */
export const CREATE_AD_V2_SCREEN_OUT_MS = 220;
